// Generated by tutorials/record.cjs from tutorials/articles/h13-settings-and-preferences.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h13 --text-only` (wording) or
// `node tutorials/record.cjs h13` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'settings-and-preferences',
  catalogId: 'h13',
  title: 'Your settings and preferences',
  summary: 'Make Dietech Online yours: bigger text, a darker screen, the facility and the page it opens on when you sign in, and a new password.',
  audience: 'Everyone who signs in to Dietech Online',
  area: 'general',
  learn: [
    'Open your settings',
    'Make the text and everything on screen bigger',
    'Choose a light or dark screen, and high contrast',
    'Pick the facility and the page Dietech opens at sign-in',
    'Change your password',
  ],
  keywords: [
    'settings',
    'my settings',
    'user settings',
    'preferences',
    'text size',
    'font size',
    'bigger text',
    'zoom',
    'ui density',
    'dark mode',
    'theme',
    'high contrast',
    'default facility',
    'start screen',
    'start page',
    'password',
    'change password',
  ],
  steps: [
    {
      id: 'open-settings',
      part: 'Open your settings',
      heading: 'Open your settings',
      text: 'Select your name at the top right, then My Settings. My Settings is also in the menu, under Setup.\n\nChanges save as soon as you make them, so there is no Save button.',
      media: {
        image: {
          src: '/help/settings-and-preferences/open-settings.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/open-settings.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.7,
        },
        alt: 'The settings page, open on Appearance: Theme, High Contrast, UI Density, Font, Field labels and Sidebar.',
      },
    },
    {
      id: 'text-size',
      part: 'Text size and colors',
      heading: 'Make everything bigger',
      text: 'Under Appearance, UI Density sets the size of the text and everything else on screen. Choose Large or X-Large if the text is hard to read. Compact fits more on a small screen.\n\nThe same choice is in the menu under your name, as UI Size.',
      media: {
        image: {
          src: '/help/settings-and-preferences/text-size.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/text-size.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'UI Density set to Large: the whole settings page is drawn bigger.',
      },
    },
    {
      id: 'theme',
      part: 'Text size and colors',
      heading: 'Light, dark and high contrast',
      text: 'Theme switches the screen between light and dark. High Contrast makes text, borders and the outline around what you have selected stronger and easier to see.',
      media: {
        image: {
          src: '/help/settings-and-preferences/theme.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Theme row with Light and Dark, and the High Contrast row with Off and On, marked.',
      },
    },
    {
      id: 'defaults',
      part: 'Where Dietech opens',
      heading: 'Open Defaults',
      text: 'Select Defaults. These choices decide where Dietech opens when you sign in, and how some pages first look to you.',
      media: {
        image: {
          src: '/help/settings-and-preferences/defaults.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/defaults.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The Defaults section: Default Facility, Start Screen, Dashboard Time Range, Reports View and the Selective Touch choices.',
      },
    },
    {
      id: 'default-facility',
      part: 'Where Dietech opens',
      heading: 'Your default facility',
      text: 'If you work in more than one facility, Default Facility is the one Dietech opens at sign-in. Pick the one you work in most.',
      media: {
        image: {
          src: '/help/settings-and-preferences/default-facility.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/default-facility.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Default Facility list open, offering None, Cedar Ridge Health Campus and Maple Grove Care Center.',
      },
    },
    {
      id: 'start-screen',
      part: 'Where Dietech opens',
      heading: 'The page you start on',
      text: 'Start Screen is the page Dietech opens after you sign in, such as Dashboard or Residents.\n\nDashboard Time Range, just below, sets how far back your dashboard looks when it opens.',
      media: {
        image: {
          src: '/help/settings-and-preferences/start-screen.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/start-screen.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'Start Screen changed to Dashboard, with Dashboard Time Range below it.',
      },
    },
    {
      id: 'password',
      part: 'Your password',
      heading: 'Change your password',
      text: 'Select Account, then Change Password. The sign-in page opens, where you set your new password. It may ask for your current password first.',
      media: {
        image: {
          src: '/help/settings-and-preferences/password.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Account section with the Change Password button marked.',
      },
    },
    {
      id: 'search-settings',
      part: 'Your password',
      heading: 'Find a setting',
      text: 'Not sure where a setting is? Type in Search settings at the top of the list, such as "font". Only the sections that have it are left in the list. Select one to open it.',
      media: {
        image: {
          src: '/help/settings-and-preferences/search-settings.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/settings-and-preferences/search-settings.mp4',
          width: 1280,
          height: 748,
          durationSec: 5,
        },
        alt: 'Search settings with "font" typed, only Appearance left in the list, and the Appearance section open with its Font choices.',
      },
    },
  ],
  tips: [
    'Text too small on a tablet or a far-away screen? Choose Large or X-Large under UI Density. Every page grows, not just one.',
    'Your settings follow you, not the computer. They are the same wherever you sign in.',
    'Forgot your password? Use the link on the sign-in page, or ask your administrator to reset it.',
  ],
  notes: [
    'Changes save as soon as you make them. There is no Save button.',
    'Some settings, such as Dashboard Time Range and Reports View, only change how pages look to you. They never change your facility\'s data.',
  ],
  related: ['getting-around', 'switching-facility', 'your-dashboard'],
  recorded: '2026-10-25',
};

export default article;
